<template>
  <div class="space-y-6">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">MCP Gateway</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Manage tools, virtual servers, and MCP protocol
        </p>
      </div>
    </div>

    <!-- Main tabs -->
    <div class="border-b border-gray-200 dark:border-gray-700">
      <nav class="-mb-px flex space-x-8" aria-label="Tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          @click="setTab(tab.key)"
          :class="[
            activeTab === tab.key
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500',
            'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm',
          ]"
        >
          {{ tab.label }}
        </button>
      </nav>
    </div>

    <!-- Tab content -->
    <div class="mt-6">
      <!-- Tools tab -->
      <div v-if="activeTab === 'tools'">
        <div class="border-b border-gray-200 dark:border-gray-700 mb-4">
          <nav class="-mb-px flex space-x-6" aria-label="Tool sub-tabs">
            <button
              v-for="sub in toolsSubTabs"
              :key="sub.key"
              @click="setSubTab('tools', sub.key)"
              :class="[
                activeSubTab === sub.key
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500',
                'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-xs',
              ]"
            >
              {{ sub.label }}
            </button>
          </nav>
        </div>

        <BaseToolsGrid v-if="activeSubTab === 'base'" :org-id="orgId"
          @view-detail="handleViewToolDetail($event)"
        />
        <CustomToolsGrid v-else-if="activeSubTab === 'custom'"
          @create="showCreateToolModal = true"
          @edit="handleEditTool"
          @view-detail="handleViewToolDetail($event, true)"
          @delete="handleDeleteTool"
          @builds="handleViewBuilds"
          @versions="handleViewVersions"
        />
        <MarketplaceTab v-else-if="activeSubTab === 'marketplace'" />
      </div>

      <!-- Virtual Servers tab -->
      <div v-else-if="activeTab === 'vservers'">
        <VServerGrid
          @create="vserverModalMode = 'create'; editingVServer = null; showVServerModal = true"
          @edit="handleEditVServer"
          @delete="handleDeleteVServer"
          @manage-tools="handleManageTools"
          @toggle-active="handleToggleVServerActive"
        />
      </div>

      <!-- Protocol tab -->
      <div v-else-if="activeTab === 'protocol'">
        <div class="border-b border-gray-200 dark:border-gray-700 mb-4">
          <nav class="-mb-px flex space-x-6" aria-label="Protocol sub-tabs">
            <button
              v-for="sub in protocolSubTabs"
              :key="sub.key"
              @click="setSubTab('protocol', sub.key)"
              :class="[
                activeSubTab === sub.key
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500',
                'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-xs',
              ]"
            >
              {{ sub.label }}
            </button>
          </nav>
        </div>

        <ResourceBrowser v-if="activeSubTab === 'resources'" :client="protocolClient" />
        <PromptManager v-else-if="activeSubTab === 'prompts'" :client="protocolClient" />
        <TaskMonitor v-else-if="activeSubTab === 'tasks'" />
      </div>

    </div>

    <!-- Modals -->
    <CreateToolModal
      :open="showCreateToolModal"
      @update:open="showCreateToolModal = $event"
      @submit="handleCreateTool"
    />
    <CreateToolModal
      :open="showEditToolModal"
      @update:open="showEditToolModal = $event"
      @submit="handleUpdateTool"
    />
    <VServerFormModal
      :open="showVServerModal"
      :mode="vserverModalMode"
      :server="editingVServer"
      :org-slug="orgSlug"
      @update:open="showVServerModal = $event"
      @submit="handleVServerSubmit"
    />
    <VServerToolsModal
      :open="showToolsModal"
      :server="toolsModalServer"
      :org-id="orgId"
      @update:open="showToolsModal = $event"
    />
    <ToolDetailDrawer
      :open="showToolDetail"
      :tool="selectedTool"
      :org-id="orgId"
      :show-env-secrets="toolDetailShowEnv"
      @update:open="showToolDetail = $event"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { storeToRefs } from 'pinia';
  import { ROUTE_NAMES } from '@/router/types';
  import { useOrganizationsStore } from '@/stores/organizations';
  import { useMcpToolsStore } from '@/stores/mcpTools';
  import { useMcpVServersStore } from '@/stores/mcpVServers';
  import { McpProtocolClient } from '@/lib/mcp/protocolClient';
  import { MCP_GATEWAY_BASE_URL } from '@/lib/api/config';
  import type {
    BaseTool,
    CustomTool,
    VirtualServer,
    VirtualServerCreate,
    VirtualServerUpdate,
    BuildFromSourcePayload,
  } from '@/lib/mcp/types';

  // Tools components
  import BaseToolsGrid from '../components/tools/BaseToolsGrid.vue';
  import CustomToolsGrid from '../components/tools/CustomToolsGrid.vue';
  import MarketplaceTab from '../components/tools/MarketplaceTab.vue';
  import CreateToolModal from '../components/tools/CreateToolModal.vue';
  import ToolDetailDrawer from '../components/tools/ToolDetailDrawer.vue';

  // VServer components
  import VServerGrid from '../components/vservers/VServerGrid.vue';
  import VServerFormModal from '../components/vservers/VServerFormModal.vue';
  import VServerToolsModal from '../components/vservers/VServerToolsModal.vue';

  // Protocol components
  import ResourceBrowser from '../components/protocol/ResourceBrowser.vue';
  import PromptManager from '../components/protocol/PromptManager.vue';
  import TaskMonitor from '../components/protocol/TaskMonitor.vue';

  // ---------------------------------------------------------------------------
  // Types
  // ---------------------------------------------------------------------------

  type MainTab = 'tools' | 'vservers' | 'protocol';
  type SubTab = string;

  interface TabDef {
    key: MainTab;
    label: string;
  }

  interface SubTabDef {
    key: string;
    label: string;
  }

  // ---------------------------------------------------------------------------
  // Constants
  // ---------------------------------------------------------------------------

  const tabs: TabDef[] = [
    { key: 'tools', label: 'Tools' },
    { key: 'vservers', label: 'Virtual Servers' },
    { key: 'protocol', label: 'Protocol' },
  ];

  const toolsSubTabs: SubTabDef[] = [
    { key: 'base', label: 'Base Tools' },
    { key: 'custom', label: 'Custom Tools' },
    { key: 'marketplace', label: 'Marketplace' },
  ];

  const protocolSubTabs: SubTabDef[] = [
    { key: 'resources', label: 'Resources' },
    { key: 'prompts', label: 'Prompts' },
    { key: 'tasks', label: 'Tasks' },
  ];

  /** Default sub-tab for each main tab that has sub-tabs. */
  const defaultSubTabs: Record<string, string> = {
    tools: 'base',
    protocol: 'resources',
  };

  const ALLOWED_TABS = new Set<string>(tabs.map((t) => t.key));

  // ---------------------------------------------------------------------------
  // Stores & routing
  // ---------------------------------------------------------------------------

  const route = useRoute();
  const router = useRouter();
  const orgStore = useOrganizationsStore();
  const toolsStore = useMcpToolsStore();
  const vserversStore = useMcpVServersStore();

  const { currentOrganizationId, currentOrganization } = storeToRefs(orgStore);

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const activeTab = ref<MainTab>('tools');
  const activeSubTab = ref<SubTab>('base');

  const orgId = computed(() => currentOrganizationId.value ?? '');
  const orgSlug = computed(() => currentOrganization.value?.slug ?? '');
  // Protocol client — created once, re-used across protocol sub-tabs
  const protocolClient = computed(() => {
    const base = MCP_GATEWAY_BASE_URL ?? '';
    return new McpProtocolClient(base);
  });

  // ---------------------------------------------------------------------------
  // Tab navigation
  // ---------------------------------------------------------------------------

  function setTab(tab: MainTab) {
    activeTab.value = tab;
    // Reset sub-tab to default when switching main tabs
    activeSubTab.value = defaultSubTabs[tab] ?? '';
    syncQueryParams();
  }

  function setSubTab(_parentTab: string, sub: string) {
    activeSubTab.value = sub;
    syncQueryParams();
  }

  function syncQueryParams() {
    const query: Record<string, string> = { tab: activeTab.value };
    if (activeSubTab.value && defaultSubTabs[activeTab.value]) {
      query.subtab = activeSubTab.value;
    }
    router.replace({ query });
  }

  function readQueryParams() {
    const tabParam = route.query.tab;
    if (typeof tabParam === 'string' && ALLOWED_TABS.has(tabParam)) {
      activeTab.value = tabParam as MainTab;
    }

    const subTabParam = route.query.subtab;
    if (typeof subTabParam === 'string' && subTabParam) {
      activeSubTab.value = subTabParam;
    } else {
      activeSubTab.value = defaultSubTabs[activeTab.value] ?? '';
    }
  }

  // ---------------------------------------------------------------------------
  // Watchers
  // ---------------------------------------------------------------------------

  watch(
    () => route.query,
    () => readQueryParams(),
  );

  watch(
    () => currentOrganizationId.value,
    async (newOrgId) => {
      if (newOrgId) {
        await Promise.all([
          toolsStore.fetchBaseTools(newOrgId),
          toolsStore.fetchCustomTools(newOrgId),
          vserversStore.fetchVServers(newOrgId),
        ]);
      }
    },
  );

  // ---------------------------------------------------------------------------
  // Modal state
  // ---------------------------------------------------------------------------

  const showCreateToolModal = ref(false);
  const showVServerModal = ref(false);
  const vserverModalMode = ref<'create' | 'edit'>('create');
  const editingVServer = ref<VirtualServer | null>(null);
  const showToolsModal = ref(false);
  const toolsModalServer = ref<VirtualServer | null>(null);
  const showToolDetail = ref(false);
  const selectedTool = ref<BaseTool | CustomTool | null>(null);
  const toolDetailShowEnv = ref(false);
  const editingTool = ref<CustomTool | null>(null);
  const showEditToolModal = ref(false);

  async function handleCreateTool(payload: BuildFromSourcePayload) {
    try {
      await toolsStore.createCustomTool(orgId.value, payload);
      showCreateToolModal.value = false;
    } catch (err) {
      console.error('Failed to create tool:', err);
    }
  }

  function handleViewToolDetail(tool: BaseTool | CustomTool, showEnv = false) {
    selectedTool.value = tool;
    toolDetailShowEnv.value = showEnv;
    showToolDetail.value = true;
  }

  function handleEditTool(tool: CustomTool) {
    editingTool.value = tool;
    showEditToolModal.value = true;
  }

  async function handleUpdateTool(payload: BuildFromSourcePayload) {
    if (!editingTool.value) return;
    try {
      await toolsStore.updateCustomTool(orgId.value, editingTool.value.id, payload);
      showEditToolModal.value = false;
      editingTool.value = null;
    } catch (err) {
      console.error('Failed to update tool:', err);
    }
  }

  function handleManageTools(server: VirtualServer) {
    toolsModalServer.value = server;
    showToolsModal.value = true;
  }

  function handleEditVServer(server: VirtualServer) {
    editingVServer.value = server;
    vserverModalMode.value = 'edit';
    showVServerModal.value = true;
  }

  async function handleVServerSubmit(
    payload:
      | { mode: 'create'; data: VirtualServerCreate }
      | { mode: 'edit'; id: string; data: VirtualServerUpdate },
  ) {
    try {
      if (payload.mode === 'edit') {
        await vserversStore.updateVServer(orgId.value, payload.id, payload.data);
      } else {
        await vserversStore.createVServer(orgId.value, payload.data);
      }
      showVServerModal.value = false;
      editingVServer.value = null;
    } catch (err) {
      console.error('Failed to save virtual server:', err);
    }
  }

  async function handleToggleVServerActive(server: VirtualServer) {
    try {
      await vserversStore.updateVServer(orgId.value, server.id, {
        is_active: !server.is_active,
      });
    } catch (err) {
      console.error('Failed to toggle virtual server:', err);
    }
  }

  async function handleDeleteVServer(server: VirtualServer) {
    if (!confirm(`Delete virtual server "${server.display_name || server.slug}"?`)) return;
    try {
      await vserversStore.deleteVServer(orgId.value, server.slug);
    } catch (err) {
      console.error('Failed to delete virtual server:', err);
    }
  }

  function handleViewBuilds(tool: CustomTool) {
    router.push({ name: ROUTE_NAMES.CUSTOM_TOOL_BUILDS, params: { id: tool.id } });
  }

  function handleViewVersions(tool: CustomTool) {
    router.push({ name: ROUTE_NAMES.CUSTOM_TOOL_VERSIONS, params: { id: tool.id } });
  }

  async function handleDeleteTool(tool: CustomTool) {
    if (!confirm(`Delete custom tool "${tool.name}"?`)) return;
    try {
      await toolsStore.deleteCustomTool(orgId.value, tool.id);
    } catch (err) {
      console.error('Failed to delete tool:', err);
    }
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  onMounted(async () => {
    readQueryParams();

    const oid = orgId.value;
    if (oid) {
      await Promise.all([
        toolsStore.fetchBaseTools(oid),
        toolsStore.fetchCustomTools(oid),
        vserversStore.fetchVServers(oid),
      ]);
    }
  });
</script>
